import type { AgentUIMessage, ChatMessageMetadata, ChatMessageRecord, ChatSource, ChatTimelineEntry } from './types';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeMessageMetadata(metadata: unknown): ChatMessageMetadata | undefined {
  if (!isObject(metadata)) return undefined;
  return metadata as ChatMessageMetadata;
}

function normalizeParts(parts: unknown): AgentUIMessage['parts'] {
  return Array.isArray(parts) ? (parts as AgentUIMessage['parts']) : [];
}

export function recordToUIMessage(record: ChatMessageRecord): AgentUIMessage {
  return {
    id: record.id,
    role: record.role,
    parts: normalizeParts(record.parts),
    metadata: normalizeMessageMetadata(record.metadata),
  };
}

export function extractMessageText(message: AgentUIMessage): string {
  return message.parts
    .filter((part): part is { type: 'text'; text: string } => part.type === 'text' && typeof part.text === 'string')
    .map(part => part.text)
    .join('\n\n')
    .trim();
}

function sourceFromOutputItem(output: unknown): ChatSource | null {
  if (!isObject(output)) return null;

  const webpageId = typeof output.id === 'string' ? output.id : null;
  const title = typeof output.title === 'string' ? output.title : null;
  const externalUrl = typeof output.url === 'string' ? output.url : null;
  if (!webpageId || !title || !externalUrl) return null;

  const snippetCandidate =
    typeof output.snippet === 'string'
      ? output.snippet
      : typeof output.description === 'string'
      ? output.description
      : typeof output.notes === 'string'
      ? output.notes
      : typeof output.content === 'string'
      ? output.content
      : null;

  return {
    webpageId,
    title,
    externalUrl,
    snippet: snippetCandidate ? snippetCandidate.slice(0, 220) : null,
  };
}

function sourcesFromToolOutput(part: unknown): ChatSource[] {
  if (!isObject(part)) return [];
  if (typeof part.type !== 'string' || !part.type.startsWith('tool-')) return [];
  if (part.state !== 'output-available') return [];

  const output = part.output;
  if (Array.isArray(output)) {
    return output.map(item => sourceFromOutputItem(item)).filter((source): source is ChatSource => Boolean(source));
  }

  const source = sourceFromOutputItem(output);
  return source ? [source] : [];
}

export function extractSources(message: AgentUIMessage): ChatSource[] {
  const metadataSources = message.metadata?.sources;
  if (Array.isArray(metadataSources)) {
    return metadataSources.filter((source): source is ChatSource => {
      return Boolean(source && typeof source.webpageId === 'string' && typeof source.title === 'string' && typeof source.externalUrl === 'string');
    });
  }

  const map = new Map<string, ChatSource>();

  for (const part of message.parts) {
    for (const source of sourcesFromToolOutput(part)) {
      map.set(source.webpageId, source);
    }
  }

  return Array.from(map.values());
}

function timelineFromToolPart(part: unknown): ChatTimelineEntry | null {
  if (!isObject(part)) return null;
  if (part.type === 'step-start' || part.type === 'start-step') {
    return {
      type: 'step',
      state: 'started',
    };
  }

  if (typeof part.type !== 'string' || !part.type.startsWith('tool-')) return null;

  const toolName = part.type.replace('tool-', '');
  const state = typeof part.state === 'string' ? part.state : 'unknown';

  if (toolName === 'searchWebpages') {
    const input = isObject(part.input) ? part.input : null;
    const output = isObject(part.output) ? part.output : null;
    const query = typeof input?.query === 'string' ? input.query : null;
    const resultCount = Array.isArray(output) ? output.length : undefined;

    return {
      type: 'search',
      state,
      query,
      resultCount,
    };
  }

  if (toolName === 'listRecentWebpages') {
    const output = Array.isArray(part.output) ? part.output : null;
    const resultCount = output ? output.length : undefined;

    return {
      type: 'recent',
      state,
      resultCount,
    };
  }

  if (toolName === 'getWebpage') {
    const output = isObject(part.output) ? part.output : null;
    const title = typeof output?.title === 'string' ? output.title : undefined;
    const webpageId = typeof output?.id === 'string' ? output.id : undefined;

    return {
      type: 'read',
      state,
      title,
      webpageId,
    };
  }

  return {
    type: toolName,
    state,
  };
}

export function extractTimeline(message: AgentUIMessage): ChatTimelineEntry[] {
  if (Array.isArray(message.metadata?.timeline)) {
    return message.metadata.timeline;
  }

  const timeline: ChatTimelineEntry[] = [];
  for (const part of message.parts) {
    const entry = timelineFromToolPart(part);
    if (entry) {
      timeline.push(entry);
    }
  }
  return timeline;
}
